import { Config } from 'tailwindcss';
import plugin from 'tailwindcss/plugin';
import prose from '@tailwindcss/typography';
import ariaAttributes from 'tailwindcss-aria-attributes';

function getTintVariantColor(color: string, intensity: number) {
  if (intensity === 0) return color;
  if (intensity > 0) {
    return `color-mix(in srgb, ${color}, white ${intensity * 100}%)`;
  }

  return `color-mix(in srgb, ${color}, black ${intensity * -1 * 100}%)`;
}

const tintVariants = {
  50: 0.95, //95%
  100: 0.9, //90%
  200: 0.7, //70%
  300: 0.5, //50%
  400: 0.3, //30%
  500: 0, //0%
  600: -0.3, //-30%
  700: -0.5, //-50%
  800: -0.7, //-70%
  900: -0.9, //-90%
};

const generateAllTintVariants = ({
  colorName,
  color,
}: {
  colorName: string;
  color: string;
}) => {
  const test = Object.entries(tintVariants).map(([key, value]) => {
    return {
      [`${colorName}-${key}`]: getTintVariantColor(color, value),
    };
  });

  return Object.assign({}, ...test);
};

export default {
  content: ['./index.html', './src/**/*.{js,ts,jsx,tsx}'],
  theme: {
    extend: {
      colors: {
        ...generateAllTintVariants({
          colorName: 'theme-colors-primary',
          color: 'var(--theme-colors-primary)',
        }),
        ...generateAllTintVariants({
          colorName: 'theme-colors-secondary',
          color: 'var(--theme-colors-secondary)',
        }),
        positive: {
          50: '#F4FDF3',
          500: '#008552',
        },
        negative: {
          50: '#FFF0F0',
          500: '#D42D2D',
        },
        attention: {
          50: '#FEFCEB',
          500: '#BA5900',
        },
        prominence: {
          50: '#F4FAFF',
          500: '#0371DF',
        },
        neutral: {
          0: '#FFFFFF',
          50: '#F9F9F9',
          100: '#F1F1F1',
          200: '#E9E9E9',
          300: '#CDCDCD',
          400: '#A5A5A5',
          500: '#767676',
          550: '#5F5F5F',
          600: '#4A4A4A',
          700: '#2B2B2B',
          800: '#191919',
          900: '#111111',
        },
      },
      placeholderColor: {
        neutral: {
          550: '#5F5F5F',
        },
      },
      borderWidth: {
        1: '1px',
        12: '12px',
      },
      fontFamily: {
        sans: 'var(--theme-font-family)',
      },
      outlineOffset: {
        10: '10px',
      },
      fontSize: {
        'body-sm': ['0.75rem', { lineHeight: '1rem', fontWeight: 400 }],
        'body-md': ['0.875rem', { lineHeight: '1.25rem', fontWeight: 400 }],
        'body-lg': ['1rem', { lineHeight: '1.5rem', fontWeight: 400 }],
        'label-sm': ['0.6875rem', { lineHeight: '1rem', fontWeight: 700 }],
        'label-md': ['0.75rem', { lineHeight: '1rem', fontWeight: 700 }],
        'label-lg': ['0.875rem', { lineHeight: '1.25rem', fontWeight: 600 }],
        'title-sm': ['0.875rem', { lineHeight: '1.25rem', fontWeight: 500 }],
        'title-md': ['1rem', { lineHeight: '1.5rem', fontWeight: 700 }],
        'title-lg': ['1.25rem', { lineHeight: '1.75rem', fontWeight: 700 }],
        'headline-sm': ['1.5rem', { lineHeight: '2rem', fontWeight: 700 }],
        'headline-md': ['1.75rem', { lineHeight: '2.25rem', fontWeight: 700 }],
        'headline-lg': ['2rem', { lineHeight: '2.5rem', fontWeight: 700 }],
        'display-sm': ['2.25rem', { lineHeight: '2.75rem', fontWeight: 700 }],
        'display-md': ['2.75rem', { lineHeight: '3.25rem' }],
        'display-lg': ['3.75rem', { lineHeight: '4rem' }],
      },
      gridTemplateColumns: {
        fluid: 'repeat(auto-fit, 312px)',
      },
      keyframes: {
        hide: {
          from: { opacity: '1' },
          to: { opacity: '0' },
        },
        slideIn: {
          from: {
            transform: 'translateX(calc(100% + var(--viewport-padding)))',
          },
          to: { transform: 'translateX(0)' },
        },
        swipeOut: {
          from: { transform: 'translateX(var(--radix-toast-swipe-end-x))' },
          to: { transform: 'translateX(calc(100% + var(--viewport-padding)))' },
        },
        sheetOverlayShow: {
          from: { opacity: '0' },
          to: { opacity: '0.1' },
        },
        sheetContentSlideIn: {
          from: { transform: 'translateX(100%)' },
          to: { transform: 'translateX(0)' },
        },
        sheetContentSlideOut: {
          from: { transform: 'translateX(0)' },
          to: { transform: 'translateX(100%)' },
        },
        sheetContentSlideInRtl: {
          from: { transform: 'translateX(-100%)' },
          to: { transform: 'translateX(0)' },
        },
        sheetContentSlideOutRtl: {
          from: { transform: 'translateX(0)' },
          to: { transform: 'translateX(-100%)' },
        },
      },
      animation: {
        hide: 'hide 150ms ease-in',
        slideIn: 'slideIn 150ms cubic-bezier(0.16, 1, 0.3, 1)',
        swipeOut: 'swipeOut 150ms ease-out',
        sheetOverlayShow: 'sheetOverlayShow 300ms ease',
        sheetContentSlideIn: 'sheetContentSlideIn 250ms ease-out',
        sheetContentSlideOut: 'sheetContentSlideOut 250ms ease-in',
        sheetContentSlideInRtl: 'sheetContentSlideInRtl 250ms ease-out',
        sheetContentSlideOutRtl: 'sheetContentSlideOutRtl 250ms ease-in',
      },
    },
    boxShadow: {
      50: '0px 2px 2px 0px rgba(0, 0, 0, 0.10)',
      100: '0px 4px 4px 0px rgba(0, 0, 0, 0.10)',
    },
    container: {
      center: true,
    },
  },
  plugins: [
    prose,
    ariaAttributes,
    plugin(({ addBase, theme }) => {
      addBase({
        html: { color: theme('colors.neutral.900') },
        'input, input[type="search"], button, a, div[role=menuitem]': {
          outlineOffset: '4px',
          outlineWidth: '2px',
        },
      });
    }),
  ],
} satisfies Config;
